import { useEffect, useRef, useState } from 'react';
import {
  CONSENT_OPEN_EVENT,
  privacyPolicyUrl,
  readConsent,
  writeConsent,
  type ConsentState,
} from '../../lib/consent';
import { applyConsent, initAnalytics, isAnalyticsAvailable } from '../../lib/analytics';

/* Field System cookie banner. Non-modal and pinned to the bottom of the
   viewport; accept and reject carry equal weight. Shown on first visit, and
   again whenever "cookie settings" fires CONSENT_OPEN_EVENT. Only rendered
   where analytics can actually run — without it there is nothing to consent to. */
export default function ConsentBanner() {
  const available = isAnalyticsAvailable();
  const [open, setOpen] = useState(() => available && readConsent() === null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const reopenedRef = useRef(false);

  useEffect(() => {
    if (!available) return;
    // A returning visitor who already accepted: start analytics for this page.
    if (readConsent() === 'granted') void initAnalytics();

    const handleOpen = () => {
      reopenedRef.current = true;
      setOpen(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, handleOpen);
  }, [available]);

  useEffect(() => {
    if (open && reopenedRef.current) firstButtonRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const choose = (state: ConsentState) => {
    writeConsent(state);
    void applyConsent(state);
    reopenedRef.current = false;
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Escape only dismisses once a choice exists — a first visit needs an answer.
    if (event.key === 'Escape' && readConsent() !== null) setOpen(false);
  };

  return (
    <div
      role="region"
      aria-labelledby="consent-title"
      aria-describedby="consent-body"
      onKeyDown={handleKeyDown}
      className="fixed inset-x-0 bottom-0 z-[400] p-4"
    >
      <div className="mx-auto max-w-3xl bg-card border border-border rounded-sm px-5 py-4 sm:flex sm:items-center sm:gap-6">
        <div className="flex-1">
          <p
            id="consent-title"
            className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground mb-2"
          >
            Cookies
          </p>
          <p id="consent-body" className="font-body text-sm text-foreground/80 leading-relaxed">
            We use essential storage to keep you signed in. With your permission we'd also use
            Google Analytics cookies to understand how noctua is used. No ads, and we never sell
            your data.{' '}
            <a
              href={privacyPolicyUrl(window.location.hostname)}
              className="underline underline-offset-2 text-foreground hover:text-primary"
            >
              privacy policy
            </a>
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2 shrink-0">
          <button
            ref={firstButtonRef}
            type="button"
            onClick={() => choose('denied')}
            className={BUTTON_CLASS}
          >
            reject
          </button>
          <button type="button" onClick={() => choose('granted')} className={BUTTON_CLASS}>
            accept
          </button>
        </div>
      </div>
    </div>
  );
}

// Identical styling for both choices — no nudging towards "accept".
const BUTTON_CLASS =
  'inline-flex items-center justify-center min-h-[44px] px-5 font-mono text-xs lowercase tracking-[0.08em] rounded-sm bg-secondary text-foreground border border-border hover:border-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background';
